import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAccount } from './AccountContext';
import {
  prefetchWorkspaceCompanies,
  readWorkspaceCompaniesCache,
} from '../utils/workspaceCompanies';

const PERSONAL = { type: 'personal', id: null, name: 'Personal', role: null };
const ADMIN = { type: 'admin', id: null, name: 'Admin', role: 'admin' };

/** Which workspace you were last in, so a reload does not silently drop you
 *  back into Personal while you are working inside a company. */
const STORAGE_KEY = 'velox-workspace';

function companyIdFromPath(pathname) {
  const match = /^\/enterprise-app\/([^/]+)/.exec(pathname || '');
  return match ? match[1] : null;
}

function isAdminPath(pathname) {
  return /^\/admin(\/|$)/.test(pathname || '');
}

/** Personal hub home — must not keep company chrome while this path is shown. */
function isPersonalHomePath(pathname) {
  return pathname === '/home' || pathname === '/home/';
}

function readStoredWorkspace() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.type === 'admin') return { ...ADMIN };
    if (parsed?.type !== 'company' || !parsed.id) return null;
    return {
      type: 'company',
      id: String(parsed.id),
      name: parsed.name || 'Company',
      role: parsed.role || null,
      isOwner: !!parsed.isOwner,
      isAdmin: !!parsed.isAdmin,
    };
  } catch {
    return null;
  }
}

function companiesForCurrentSession() {
  if (!localStorage.getItem('token')) return [];
  return readWorkspaceCompaniesCache();
}

const WorkspaceContext = createContext(null);

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return ctx;
}

export function WorkspaceProvider({ children }) {
  const location = useLocation();
  const { account } = useAccount();
  const [companies, setCompanies] = useState(companiesForCurrentSession);
  const [selected, setSelected] = useState(() => readStoredWorkspace() || PERSONAL);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(() => companiesForCurrentSession().length > 0);
  // Cache is allowed to paint the switcher; only the live list may decide
  // that a stored company is gone.
  const [listConfirmed, setListConfirmed] = useState(false);

  // Which token we last loaded for, so a sign-in after /login picks companies
  // up without remounting, and a failed anonymous load does not count as
  // "you have no companies".
  const loadedForTokenRef = useRef(null);

  const load = useCallback(async ({ force = false } = {}) => {
    const token = localStorage.getItem('token');

    if (!token) {
      loadedForTokenRef.current = null;
      setCompanies([]);
      setIsLoading(false);
      setHasLoaded(false);
      setListConfirmed(false);
      return;
    }

    if (!force && loadedForTokenRef.current === token) return;

    const cached = readWorkspaceCompaniesCache();
    if (cached.length) {
      setCompanies(cached);
      setHasLoaded(true);
    }

    loadedForTokenRef.current = token;
    if (!cached.length) setIsLoading(true);

    try {
      const list = await prefetchWorkspaceCompanies();
      if (loadedForTokenRef.current !== token) return;
      setCompanies(list);
      setHasLoaded(true);
      setListConfirmed(true);
    } finally {
      if (loadedForTokenRef.current === token) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Re-checked on navigation so signing in (which routes to /home) picks the
    // new token up without the provider having to remount.
  }, [load, location.pathname]);

  // A company or admin URL decides the workspace on the first render rather
  // than after a request resolves, so opening those pages never paints the
  // wrong navigation first.
  const routeCompanyId = companyIdFromPath(location.pathname);
  const routeIsAdmin = isAdminPath(location.pathname);
  const routeIsPersonalHome = isPersonalHomePath(location.pathname);

  const current = useMemo(() => {
    if (routeCompanyId) {
      const known = companies.find((c) => c.id === routeCompanyId);
      const carried = selected.id === routeCompanyId ? selected : null;
      return {
        type: 'company',
        id: routeCompanyId,
        name: known?.name || carried?.name || 'Company',
        role: known?.role || carried?.role || null,
        isOwner: known?.isOwner ?? carried?.isOwner ?? false,
        isAdmin: known?.isAdmin ?? carried?.isAdmin ?? false,
      };
    }
    // Personal Home is never shown under company chrome, even if a company
    // was previously selected (direct link /home, post-login, etc.).
    if (routeIsPersonalHome) return PERSONAL;
    if (routeIsAdmin || selected.type === 'admin') return ADMIN;
    if (selected.type !== 'company' || !selected.id) return PERSONAL;
    const known = companies.find((c) => c.id === selected.id);
    return {
      type: 'company',
      id: selected.id,
      name: known?.name || selected.name || 'Company',
      role: known?.role || selected.role || null,
      isOwner: known?.isOwner ?? selected.isOwner ?? false,
      isAdmin: known?.isAdmin ?? selected.isAdmin ?? false,
    };
  }, [routeCompanyId, routeIsAdmin, routeIsPersonalHome, selected, companies]);

  // Leaving a company or admin page keeps you in that workspace, so the choice
  // has to follow the URL rather than the URL following a stale choice.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  useEffect(() => {
    if (routeCompanyId && selectedRef.current.id !== routeCompanyId) {
      setSelected({ type: 'company', id: routeCompanyId, name: 'Company', role: null });
    }
  }, [routeCompanyId]);

  useEffect(() => {
    if (routeIsAdmin && selectedRef.current.type !== 'admin') {
      setSelected(ADMIN);
    }
  }, [routeIsAdmin]);

  // Persist Personal when /home is open so a later non-routed page does not
  // resurrect the previous company selection from memory.
  useEffect(() => {
    if (!routeIsPersonalHome) return;
    if (selectedRef.current.type !== 'personal') {
      setSelected(PERSONAL);
    }
  }, [routeIsPersonalHome]);

  const selectPersonal = useCallback(() => setSelected(PERSONAL), []);
  const selectAdmin = useCallback(() => setSelected(ADMIN), []);

  const selectCompany = useCallback((id, extras = {}) => {
    if (!id) {
      setSelected(PERSONAL);
      return;
    }
    setSelected({
      type: 'company',
      id: String(id),
      name: extras.name || 'Company',
      role: extras.role || null,
      isOwner: !!extras.isOwner,
      isAdmin: !!extras.isAdmin,
    });
  }, []);

  // A workspace you are no longer a member of must not linger in the switcher
  // or keep company navigation on screen. Only decide that after the live list
  // for this token — a cached or anonymous empty list is not an answer.
  useEffect(() => {
    if (!listConfirmed || routeCompanyId || routeIsAdmin) return;
    if (selected.type === 'company' && !companies.some((c) => c.id === selected.id)) {
      setSelected(PERSONAL);
    }
  }, [listConfirmed, companies, routeCompanyId, routeIsAdmin, selected.type, selected.id]);

  useEffect(() => {
    if (routeIsAdmin) return;
    if (selected.type === 'admin' && account && account.role !== 'admin' && account.staff?.status !== 'active' && !account.staff?.superAdmin) {
      setSelected(PERSONAL);
    }
  }, [account, selected.type, routeIsAdmin]);

  useEffect(() => {
    try {
      if (current.type === 'company' && current.id) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
      } else if (current.type === 'admin') {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(ADMIN));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // A private-mode storage failure must not break navigation.
    }
  }, [current]);

  const refresh = useCallback(() => load({ force: true }), [load]);

  const value = useMemo(
    () => ({
      current,
      companies,
      isLoading,
      hasLoaded,
      isCompany: current.type === 'company',
      isAdmin: current.type === 'admin',
      companyById: (id) => companies.find((c) => c.id === String(id)) || null,
      selectPersonal,
      selectCompany,
      selectAdmin,
      refresh,
      PERSONAL,
      ADMIN,
    }),
    [current, companies, isLoading, hasLoaded, selectPersonal, selectCompany, selectAdmin, refresh]
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export default WorkspaceContext;
